import get from 'lodash/get';
import merge from 'lodash/merge';

import { getIsAuthenticated, getProfileKey, isProfileClaimed, isProfileKnown } from './selectors';
import { getProfileById } from '../entities/profiles/state';
import profileActions from '../entities/profiles/actions';

import { destroyAllCookie } from '../../../utils/cookie';
import { reportGoogleSignInLoggedIn } from '../../../utils/reporting/googleOneTapEvents';
import { reportJobcaseSsoLogin, SSO_LOGIN_TYPE } from '../../../utils/reporting/ssoEvents';
import config from '../../../config';
import customFetch from '../../../utils/fetch';

export const TYPES = {
  LOGIN_FAILURE: 'authentication/LOGIN_FAILURE',
  LOGIN_REQUEST: 'authentication/LOGIN_REQUEST',
  LOGIN_SUCCESS: 'authentication/LOGIN_SUCCESS',
  LOGOUT: 'authentication/LOGOUT',
  SET_ARRIVAL_INITIALIZED: 'authentication/SET_ARRIVAL_INITIALIZED',
  SET_AUTH_LOADING: 'authentication/SET_AUTH_LOADING',
  SET_CURRENT_PROFILE: 'authentication/SET_CURRENT_PROFILE',
  SET_EDIT_PASSWORD_SUBMITTED: 'authentication/SET_EDIT_PASSWORD_SUBMITTED',
  SET_IS_AUTHENTICATED: 'authentication/SET_IS_AUTHENTICATED',
  SET_LOGIN_ERROR: 'authentication/SET_LOGIN_ERROR',
  SET_PROFILE_KEY: 'authentication/SET_PROFILE_KEY',
  SET_PROFILE_STATUS: 'authentication/SET_PROFILE_STATUS',
  SET_RECOVER_PASSWORD_SUBMITTED: 'authentication/SET_RECOVER_PASSWORD_SUBMITTED',
  SIGNUP_FAILURE: 'authentication/SIGNUP_FAILURE',
  SIGNUP_REQUEST: 'authentication/SIGNUP_REQUEST',
  SIGNUP_SUCCESS: 'authentication/SIGNUP_SUCCESS',
  UPDATING_CURRENT_PROFILE_ERROR: 'authentication/UPDATING_CURRENT_PROFILE_ERROR',
  UPDATING_CURRENT_PROFILE_SUCCESS: 'authentication/UPDATING_CURRENT_PROFILE_SUCCESS',
  UPDATING_CURRENT_PROFILE: 'authentication/UPDATING_CURRENT_PROFILE',
};

/**
 * Check if this client is authenticated, and register that status in Redux.
 *
 * @param {*} state the redux state
 * @return the profile
 */
export const setCurrentProfile = (fetchOptions = {}) => async (dispatch, getState) => {
  const state = getState();

  try {
    const isAuthenticated = getIsAuthenticated(state);
    if (!isAuthenticated) {
      return null;
    }

    const response = await customFetch(`${config.apiUrl}/v1/me`, fetchOptions);
    const profile = response.data;
    const profileKey = profile.key;

    dispatch(profileActions.set(profile));
    dispatch({ type: TYPES.SET_CURRENT_PROFILE, payload: { profileKey } });
    return profile;
  } catch (error) {
    dispatch({ type: TYPES.SET_CURRENT_PROFILE, payload: null });
    throw error;
  }
};

export const setIsAuthenticated = isAuthenticated => async (dispatch) => {
  await dispatch({ type: TYPES.SET_IS_AUTHENTICATED, payload: isAuthenticated });
};

export const updateProfile = data => async (dispatch) => {
  try {
    dispatch({ type: TYPES.UPDATING_CURRENT_PROFILE });

    const response = await customFetch(`${config.apiUrl}/v1/profiles`, {
      method: 'PATCH',
      body: data,
    });
    const profile = response.data;
    const profileKey = profile.key;

    dispatch(profileActions.set(profile));
    dispatch({ type: TYPES.UPDATING_CURRENT_PROFILE_SUCCESS, payload: profileKey });
    return profile;
  } catch (error) {
    dispatch({ type: TYPES.UPDATING_CURRENT_PROFILE_ERROR, payload: error });
    throw error;
  }
};

export const updateProfileById = (data, profileKey) => async (dispatch, getState) => {
  try {
    if (profileKey) {
      dispatch({ type: TYPES.UPDATING_CURRENT_PROFILE });

      const response = await customFetch(`${config.apiUrl}/v1/profiles/${profileKey}`, {
        method: 'PATCH',
        body: data,
      });
      let profile = response.data;

      if (data.is_unauthenticated_attributes_only) {
        const state = getState();
        const existingProfile = { ...(getProfileById(state, profileKey)?.data) };
        profile = merge(existingProfile, response.data);
      }

      dispatch(profileActions.set(profile));
      dispatch({ type: TYPES.UPDATING_CURRENT_PROFILE_SUCCESS, payload: profileKey });
      return profile;
    }
    return null;
  } catch (error) {
    dispatch({ type: TYPES.UPDATING_CURRENT_PROFILE_ERROR, payload: error });
    throw error;
  }
};

export const uploadProfilePhoto = data => async (dispatch) => {
  try {
    const profileKey = get(data, 'profileKey');
    if (profileKey) {
      const url = get(data, 'fileUploaded.url');
      const filename = get(data, 'fileUploaded.filename');
      let promise = fetch(url);
      const image = await Promise.resolve(promise);
      promise = image.blob();
      const file = await Promise.resolve(promise);
      const formData = new FormData();
      formData.append('photo', file, filename);
      const response = await customFetch(`${config.apiUrl}/v1/profiles/${profileKey}/upload-photo`, {
        method: 'POST',
        body: formData,
        formData: true,
      });
      const profile = response.data;
      dispatch(profileActions.set(profile));
      return profile;
    }
    return null;
  } catch (error) {
    throw error;
  }
};

export const initializeArrivalState = () => async (dispatch) => {
  try {
    await customFetch(`${config.apiUrl}/v1/init`, {
      method: 'GET',
    });
  } catch (error) {
    throw error;
  }

  dispatch({ type: TYPES.SET_ARRIVAL_INITIALIZED, payload: null });
};

export const initializeProfileStatus = () => async (dispatch) => {
  try {
    const response = await customFetch(`${config.apiUrl}/v1/profiles/status`, { method: 'GET' });
    const profileKey = response && response.profileKey;
    // Fetch profile if we can
    if (profileKey) {
      dispatch(setIsAuthenticated(true));
      dispatch(profileActions.get(profileKey));
    }
    dispatch({ type: TYPES.SET_PROFILE_STATUS, payload: response });
    dispatch({ type: TYPES.SET_PROFILE_KEY, payload: response });
  } catch (error) {
    throw error;
  }
};

export const getJobcaseCookies = arrivalKey => async (dispatch, getState) => {
  const state = getState();
  const profileKey = getProfileKey(state);

  try {
    customFetch(`${config.jobcaseUrl}/getcookies`, {
      method: 'POST',
      body: {
        arrival_key: arrivalKey,
        profile_key: profileKey,
      },
    });
  } catch (error) {
    throw error;
  }
};

export const googleOneTapSignIn = (idToken, arrivalKey) => async (dispatch) => {
  try {
    await customFetch(`${config.apiUrl}/v1/auth/googleonetapsignin`, {
      method: 'POST',
      body: { id_token: idToken },
    });
    await dispatch(setIsAuthenticated(true));
    const profile = await dispatch(setCurrentProfile());
    // Get jobcase cookies for rails app
    dispatch(getJobcaseCookies(arrivalKey));
    return profile;
  } catch (error) {
    throw error;
  }
};

export const attemptSignup = (data, arrivalKey) => async (dispatch, getState) => {
  dispatch({ type: TYPES.SIGNUP_REQUEST });
  const state = getState();
  const claimed = isProfileClaimed(state);
  const known = isProfileKnown(state);
  const claiming = known && !claimed;

  try {
    await customFetch(`${config.apiUrl}/v1/auth/emailsignup`, {
      method: 'POST',
      body: data,
      fullResponse: true,
      credentials: 'include',
      disableUnauthRefresh: true,
    });

    reportJobcaseSsoLogin(claiming ? SSO_LOGIN_TYPE.CLAIM : SSO_LOGIN_TYPE.STANDARD);
    await dispatch(setIsAuthenticated(true));
    await dispatch(setCurrentProfile());
    // Get jobcase cookies for rails app
    dispatch(getJobcaseCookies(arrivalKey));
    dispatch({ type: TYPES.SIGNUP_SUCCESS });
    return true;
  } catch (error) {
    // If they passed the client side validation above then they should only be
    // able to get a 401 representing account not found
    // Any other errors would be legitimate network/server errors

    if (error.error_code === 400) {
      dispatch({
        type: TYPES.SET_LOGIN_ERROR,
        payload: { email: 'The email address contains invalid word.' },
      });
    } else if (error.error_code === 401) {
      dispatch({
        type: TYPES.SET_LOGIN_ERROR,
        payload: { signup: 'The email address or password is incorrect, please try again.' },
      });
    } else {
      // If we've reached this branch, then there's a proper
      // login error (requests, etc) that needs to be thrown
      throw error;
    }
    dispatch({ type: TYPES.SIGNUP_FAILURE });
    return false;
  }
};

export const googleSignIn = (idToken, arrivalKey) => async (dispatch) => {
  try {
    await customFetch(`${config.apiUrl}/v1/auth/googlesignin`, {
      method: 'POST',
      body: { id_token: idToken },
    });
    await dispatch(setIsAuthenticated(true));
    const profile = await dispatch(setCurrentProfile());
    reportGoogleSignInLoggedIn();
    // Get jobcase cookies for rails app
    dispatch(getJobcaseCookies(arrivalKey));
    return profile;
  } catch (error) {
    throw error;
  }
};

export const attemptEditPassword = data => async (dispatch) => {
  try {
    await customFetch(`${config.apiUrl}/v1/my-profile/reset-password`, {
      method: 'PATCH',
      body: data,
      fullResponse: true,
    });
    dispatch({ type: TYPES.SET_EDIT_PASSWORD_SUBMITTED, payload: true });
  } catch (error) {
    if (error.status === 400) {
      dispatch({
        type: TYPES.SET_LOGIN_ERROR,
        payload: 'Reset password link no longer valid.',
      });
    } else {
      dispatch({
        type: TYPES.SET_LOGIN_ERROR,
        payload: 'Couldn\'t reset your password please try again later.',
      });
    }
    throw error;
  }
};

export const logout = (fetchOptions = {}) => async (dispatch) => {
  try {
    await customFetch(`${config.apiUrl}/v1/auth/session/logout`, {
      method: 'DELETE',
      ...fetchOptions,
    });
    destroyAllCookie();
    window.sessionStorage.clear();
    dispatch({ type: TYPES.LOGOUT });
  } catch (error) {
    throw error;
  }
};

export const attemptLogin = (data, arrivalKey) => async (dispatch) => {
  dispatch({ type: TYPES.LOGIN_REQUEST });
  try {
    await customFetch(`${config.apiUrl}/v1/auth/emailsignin`, {
      method: 'POST',
      body: data,
      fullResponse: true,
      credentials: 'include',
      disableUnauthRefresh: true,
    });

    await dispatch(setIsAuthenticated(true));
    await dispatch(setCurrentProfile());
    // Get jobcase cookies for rails app
    dispatch(getJobcaseCookies(arrivalKey));
    dispatch({ type: TYPES.LOGIN_SUCCESS });
    return true;
  } catch (error) {
    // If they passed the client side validation above then they should only be
    // able to get a 401 representing account not found.
    // Any other errors would be legitimate network/server errors
    if (error.error_code === 401) {
      dispatch({
        type: TYPES.SET_LOGIN_ERROR,
        payload: { login: 'The email address or password is incorrect, please try again.' },
      });
    } else {
      // If we've reached this branch, then there's a proper
      // login error (requests, etc) that needs to be thrown
      throw error;
    }
    dispatch({ type: TYPES.LOGIN_FAILURE });
    return false;
  }
};

export const attemptRecoverPassword = data => async (dispatch) => {
  try {
    await customFetch(`${config.apiUrl}/v1/my-profile/reset-password-email`, {
      method: 'PUT',
      body: data,
      fullResponse: true,
      credentials: 'include',
    });

    await dispatch({ type: TYPES.SET_RECOVER_PASSWORD_SUBMITTED, payload: true });
  } catch (error) {
    dispatch({
      type: TYPES.SET_LOGIN_ERROR,
      payload: 'Something went wrong.  Please try again.',
    });
    // If we've reached this branch, then there's a proper
    // network error (requests, etc) that needs to be thrown
    throw error;
  }
};

const setLoginError = errorMessage => async (dispatch) => {
  dispatch({
    type: TYPES.SET_LOGIN_ERROR,
    payload: errorMessage,
  });
};

const linkedInSignIn = (idToken, arrivalKey, redirectUri) => async (dispatch) => {
  dispatch({ type: TYPES.SET_AUTH_LOADING });
  try {
    await customFetch(`${config.apiUrl}/v1/auth/linkedinsignin`, {
      method: 'POST',
      body: { id_token: idToken, redirect_uri: redirectUri },
      fullResponse: true,
    });
    await dispatch(setIsAuthenticated(true));
    await dispatch(setCurrentProfile());
    // Get jobcase cookies for rails app
    dispatch(getJobcaseCookies(arrivalKey));
    return true;
  } catch (error) {
    // If no email is included with the oauth code, we'll get a 422
    if (error.status === 422) {
      dispatch({
        type: TYPES.SET_LOGIN_ERROR,
        payload: 'We could not retrieve an email address from your LinkedIn profile. Try another provider or allow LinkedIn to share your email.',
      });
    } else {
      dispatch({
        type: TYPES.SET_LOGIN_ERROR,
        payload: 'There was an error logging in with LinkedIn. Please try again.',
      });
    }
    return false;
  }
};

export default {
  TYPES,
  attemptEditPassword,
  attemptLogin,
  attemptRecoverPassword,
  attemptSignup,
  getJobcaseCookies,
  googleOneTapSignIn,
  googleSignIn,
  initializeArrivalState,
  initializeProfileStatus,
  logout,
  setCurrentProfile,
  setIsAuthenticated,
  updateProfile,
  updateProfileById,
  uploadProfilePhoto,
  setLoginError,
  linkedInSignIn,
};
